import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSunrise, FiSunset, FiStar, FiCheck, FiMessageCircle } = FiIcons;

const Packages = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const packages = [
    {
      name: "Morning Glory",
      icon: FiSunrise,
      price: "$120",
      duration: "Half Day",
      ideal: "Photographers & Early Risers",
      inclusions: [
        "Sunrise Safari (5:30 AM)",
        "Breakfast Box",
        "1 Night Stay",
        "Professional Guide (Mohan)",
        "Park Entry Fees",
        "Complimentary Tea/Coffee"
      ],
      highlight: "Best for Wildlife Photography",
      color: "from-orange-400 to-red-500"
    },
    {
      name: "Evening Gold",
      icon: FiSunset,
      price: "$130",
      duration: "Half Day",
      ideal: "Families & Relaxed Travelers",
      inclusions: [
        "Sunset Safari (2:30 PM)",
        "Traditional Dinner",
        "1 Night Stay",
        "Expert Guide (Mohan)",
        "Park Entry Fees",
        "Welcome Drink"
      ],
      highlight: "Perfect for Families",
      color: "from-yellow-400 to-orange-500"
    },
    {
      name: "The Full Ranger",
      icon: FiStar,
      price: "$220",
      duration: "Full Experience",
      ideal: "Wildlife Enthusiasts",
      inclusions: [
        "2 Safaris (AM & PM)",
        "All Meals Included",
        "2 Nights Stay",
        "Private Guide (Mohan)",
        "Park Entry Fees",
        "Elephant Transit Home Visit",
        "Local Village Tour"
      ],
      highlight: "Most Popular",
      color: "from-purple-400 to-pink-500",
      featured: true
    }
  ];

  return (
    <section id="packages" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Safari <span className="gradient-text">Packages</span>
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Choose your adventure. Each package combines comfortable accommodation 
            with expertly guided wildlife experiences.
          </p>
        </motion.div>

        {/* Packages Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              whileHover={{ scale: 1.02, y: -10 }}
              className={`relative glass-card rounded-3xl p-8 ${
                pkg.featured ? 'ring-2 ring-yellow-400/50' : ''
              }`}
            >
              {/* Featured Badge */}
              {pkg.featured && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="absolute -top-4 left-1/2 transform -translate-x-1/2"
                >
                  <div className="glass-morphism px-4 py-2 rounded-full">
                    <span className="gradient-text font-bold text-sm">
                      {pkg.highlight}
                    </span>
                  </div>
                </motion.div>
              )}

              {/* Package Header */}
              <div className="text-center mb-8">
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className={`inline-block p-4 rounded-2xl bg-gradient-to-r ${pkg.color} mb-4`}
                >
                  <SafeIcon icon={pkg.icon} className="text-3xl text-white" />
                </motion.div>
                
                <h3 className="text-2xl font-bold text-white mb-2">
                  {pkg.name}
                </h3>
                
                <div className="text-3xl font-bold gradient-text mb-2">
                  {pkg.price}
                </div>
                
                <div className="text-white/60 text-sm mb-1">
                  {pkg.duration}
                </div>
                
                <div className="text-white/80 text-sm">
                  Ideal for: {pkg.ideal}
                </div>
              </div>

              {/* Inclusions */}
              <div className="space-y-3 mb-8">
                {pkg.inclusions.map((inclusion, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.3 + idx * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <SafeIcon icon={FiCheck} className="text-green-400 text-sm flex-shrink-0" />
                    <span className="text-white/80 text-sm">{inclusion}</span>
                  </motion.div>
                ))}
              </div>

              {/* CTA Button */}
              <motion.a
                href={`https://wa.me/94771234567?text=Hi%20Mohan,%20I%20am%20interested%20in%20the%20${pkg.name}%20package`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full glass-morphism py-4 rounded-2xl text-white font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiMessageCircle} />
                <span>Check Availability</span>
              </motion.a>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-white/80 mb-4">
            Need a custom package? Mohan can create a personalized safari experience just for you.
          </p>
          <motion.a
            href="https://wa.me/94771234567?text=Hi%20Mohan,%20I%20would%20like%20to%20discuss%20a%20custom%20safari%20package"
            whileHover={{ scale: 1.05 }}
            className="inline-block glass-card px-8 py-3 rounded-full text-white font-medium hover:bg-white/10 transition-all duration-300"
          >
            Request Custom Package
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Packages;